// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { SchemaModel } from '../models/Schema'
import type { RecordModel } from '../models/Record'
import { createId, safeNanoid } from '../utils/id'

/** Minimal query interface — only .exec() is used by the adapter.
 *  The REST CollectionAccessorAdapter does not support chainable
 *  .skip()/.limit()/.sort() so the VCA applies those client-side.
 *
 *  Returns `unknown[]` because RxDB documents have `.toJSON()` while
 *  REST responses are plain objects — {@link VirtualCollectionAdapter.toPlainObject}
 *  normalizes both at runtime. */
interface ExecutableQuery {
  exec(): Promise<unknown[]>
}

/**
 * Structural interface for the subset of RxCollection methods used by the adapter.
 *
 * All operations are collection-level (not document-level) so the adapter
 * works with both RxDB local collections and REST client wrappers.
 */
export interface RecordCollection {
  find(query: {
    selector: Record<string, unknown>
    sort?: Array<Record<string, string>>
  }): ExecutableQuery
  findOne(query: { selector: Record<string, unknown> }): {
    exec(): Promise<Record<string, unknown> | null>
  }
  insert(doc: Partial<RecordModel>): Promise<Record<string, unknown>>
  /** Update a document by ID. Returns the updated document. */
  update(id: string, patch: Record<string, unknown>): Promise<Record<string, unknown>>
  /** Delete a document by ID. */
  delete(id: string): Promise<boolean>
  /**
   * Execute an aggregate query against the collection.
   * Only supported by REST-backed implementations (D1 worker).
   * RxDB local collections do not implement this method.
   *
   * @param spec - The AggregateRequest payload forwarded verbatim to the worker.
   * @returns An object with a `documents` array of result rows.
   */
  aggregate?(spec: Record<string, unknown>): Promise<{ documents: Record<string, unknown>[] }>
}

/** Structural interface for the schemas collection used by SchemaRegistry. */
export interface SchemaCollection {
  find(query: { selector: Record<string, unknown> }): {
    exec(): Promise<Array<{ toJSON(): Record<string, unknown> }>>
  }
  findOne(query: { selector: Record<string, unknown> }): {
    exec(): Promise<{ toJSON(): Record<string, unknown> } | null>
  }
}

/**
 * Fields stored as top-level columns on RecordModel.
 * Used to distinguish between fields queried/sorted at root level
 * vs those that go into the `data` bag.
 *
 * This set must exactly match the properties declared in the RecordModel
 * interface (packages/db-collections/src/models/Record.ts). When a new
 * column is added to RecordModel / the Drizzle schema, add it here too.
 *
 * Why explicit enumeration rather than deriving from Record.schema.properties
 * at runtime: VCA is instantiated in contexts (browser, REST, test) where
 * importing the Record model would create a circular dependency or require
 * a live RxDB collection reference that is not available at construction
 * time. A compile-time constant is simpler and easier to audit.
 */
const TOP_LEVEL_FIELDS = new Set([
  'id',
  'schemaId',
  'orgId',
  'workspaceId',
  'userId',
  'createdAt',
  'updatedAt',
  'metadata',
  'extended',
  'title',
  'status',
  'parentId',
  'parentCollection',
  'data',
  'archived',
  'completed',
  'completedAt',
  'startedAt',
  'sortOrder',
  'assigneeId',
  'priority',
  'dueAt',
  'amount',
  'version',
  'tenantId',
])

/**
 * Immutable fields that callers must never overwrite on an existing document.
 * These are silently dropped from update() and upsert() patch payloads.
 */
const IMMUTABLE_FIELDS = new Set([
  'id',
  'schemaId',
  'orgId',
  'workspaceId',
  'userId',
  'createdAt',
])

/**
 * Wraps the generic `records` RxCollection and scopes all operations
 * to a specific schemaId. Provides the same CRUD interface that consumers
 * expect from a dedicated collection.
 */
export class VirtualCollectionAdapter {
  constructor(
    private recordsCollection: RecordCollection,
    private schemaDefinition: SchemaModel
  ) {}

  /**
   * Safely extract a plain object from an RxDB document or REST response.
   * RxDB documents have `.toJSON()`, REST clients return plain objects.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private toPlainObject(doc: any): Record<string, unknown> {
    return (typeof doc.toJSON === 'function' ? doc.toJSON() : doc) as Record<string, unknown>
  }

  get name(): string {
    return this.schemaDefinition.name
  }

  get schemaId(): string {
    return this.schemaDefinition.id
  }

  /**
   * Map a flat document (as callers provide) into the RecordModel storage format.
   *
   * Any field present in TOP_LEVEL_FIELDS is written directly to the record
   * object. Everything else is packed into the `data` bag.
   *
   * Using TOP_LEVEL_FIELDS as the single source of truth means that schema
   * columns added to RecordModel (e.g. `extended`, `sortOrder`, `priority`)
   * are automatically promoted without needing a matching change here.
   */
  mapToRecord(flatDoc: Record<string, unknown>): Partial<RecordModel> {
    const record: Partial<RecordModel> = {
      schemaId: this.schemaDefinition.id,
      archived: false,
    }

    const data: Record<string, unknown> = {}

    for (const [key, value] of Object.entries(flatDoc)) {
      if (TOP_LEVEL_FIELDS.has(key)) {
        // Promote to a real RecordModel column. The schemaId injected above
        // takes precedence — callers must not pass a different schemaId.
        if (key !== 'schemaId') {
          ;(record as Record<string, unknown>)[key] = value
        }
      } else {
        data[key] = value
      }
    }

    // `title` and `archived` must always be present (required by the schema).
    if (record.title === undefined) record.title = ''
    record.data = data
    return record
  }

  /**
   * Map a RecordModel back to a flat document (as callers expect).
   * Merges `data` fields to top level alongside standard fields.
   */
  mapFromRecord(record: RecordModel): Record<string, unknown> {
    // Destructure to exclude RxDB internals and schemaId from the flat output
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { data, schemaId, _rev, _meta, _deleted, _attachments, ...rest } =
      record as unknown as Record<string, unknown>
    return {
      ...rest,
      ...((data as Record<string, unknown>) ?? {}),
    }
  }

  /**
   * Rewrite a selector to scope it to this schema.
   * Only includes top-level RecordModel fields that RxDB can query directly.
   * Data-bag fields are omitted — use {@link extractDataFilter} to get them
   * for client-side filtering after the query returns.
   */
  rewriteSelector(selector: Record<string, unknown>, isNested = false): Record<string, unknown> {
    // Only inject schemaId at the top level. Nested sub-selectors (inside
    // $and/$or) inherit the top-level constraint and must not duplicate it.
    const rewritten: Record<string, unknown> = isNested
      ? {}
      : { schemaId: this.schemaDefinition.id }

    for (const [key, value] of Object.entries(selector)) {
      if (key.startsWith('$')) {
        if (Array.isArray(value)) {
          const items = value
            .map((item: Record<string, unknown>) =>
              typeof item === 'object' ? this.rewriteSelector(item, true) : item
            )
            .filter(
              (item: unknown) =>
                typeof item !== 'object' || Object.keys(item as Record<string, unknown>).length > 0
            )
          if (items.length > 0) {
            rewritten[key] = items
          }
        } else {
          rewritten[key] = value
        }
      } else if (TOP_LEVEL_FIELDS.has(key)) {
        rewritten[key] = value
      }
      // Data-bag fields are intentionally omitted from the RxDB query.
      // RxDB cannot query into the generic `data` object. Use
      // extractDataFilter() + matchesDataFilter() for client-side filtering.
    }

    return rewritten
  }

  /**
   * Extract data-bag field conditions from a selector for client-side filtering.
   * Returns field conditions that are NOT top-level RecordModel fields and NOT
   * logical operators ($and/$or).
   *
   * Note: data-bag fields inside $and/$or are not extracted — they are silently
   * dropped from the RxDB query. For complex logical queries on data-bag fields,
   * fetch all records for the schema and filter entirely client-side.
   */
  extractDataFilter(selector: Record<string, unknown>): Record<string, unknown> {
    const filter: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(selector)) {
      if (!key.startsWith('$') && !TOP_LEVEL_FIELDS.has(key)) {
        filter[key] = value
      }
    }
    return filter
  }

  /**
   * Rewrite sort criteria, keeping only top-level RecordModel fields.
   * Data-bag field sorts are omitted — use {@link extractClientSort} to get
   * them for client-side sorting.
   */
  rewriteSort(sort: Array<Record<string, string>>): Array<Record<string, string>> {
    return sort
      .map(sortItem => {
        if (typeof sortItem !== 'object' || sortItem === null || Array.isArray(sortItem)) {
          return {}
        }
        const rewritten: Record<string, string> = {}
        for (const [field, direction] of Object.entries(sortItem)) {
          if (TOP_LEVEL_FIELDS.has(field)) {
            rewritten[field] = direction
          }
        }
        return rewritten
      })
      .filter(item => Object.keys(item).length > 0)
  }

  /**
   * Extract data-bag field sorts for client-side sorting.
   */
  extractClientSort(sort: Array<Record<string, string>>): Array<Record<string, string>> {
    return sort
      .map(sortItem => {
        const clientSort: Record<string, string> = {}
        for (const [field, direction] of Object.entries(sortItem)) {
          if (!TOP_LEVEL_FIELDS.has(field)) {
            clientSort[field] = direction
          }
        }
        return clientSort
      })
      .filter(item => Object.keys(item).length > 0)
  }

  /**
   * Test whether a flat (mapped) document matches data-bag filter conditions.
   * Supports direct equality and common MongoDB-style operators.
   */
  static matchesDataFilter(doc: Record<string, unknown>, filter: Record<string, unknown>): boolean {
    for (const [key, condition] of Object.entries(filter)) {
      const value = doc[key]
      if (typeof condition === 'object' && condition !== null && !Array.isArray(condition)) {
        const ops = condition as Record<string, unknown>
        for (const [op, expected] of Object.entries(ops)) {
          switch (op) {
            case '$eq':
              if (value !== expected) return false
              break
            case '$ne':
              if (value === expected) return false
              break
            case '$gt':
              if (!((value as number) > (expected as number))) return false
              break
            case '$gte':
              if (!((value as number) >= (expected as number))) return false
              break
            case '$lt':
              if (!((value as number) < (expected as number))) return false
              break
            case '$lte':
              if (!((value as number) <= (expected as number))) return false
              break
            case '$in':
              if (!Array.isArray(expected) || !expected.includes(value)) return false
              break
            case '$nin':
              if (Array.isArray(expected) && expected.includes(value)) return false
              break
            case '$exists':
              if (expected ? value === undefined : value !== undefined) return false
              break
            default:
              break
          }
        }
      } else {
        // Direct equality: { isAgent: true }
        if (value !== condition) return false
      }
    }
    return true
  }

  /**
   * Sort flat (mapped) documents by data-bag field criteria client-side.
   */
  static applyClientSort<T extends Record<string, unknown>>(
    docs: T[],
    sort: Array<Record<string, string>>
  ): T[] {
    if (sort.length === 0) return docs
    return [...docs].sort((a, b) => {
      for (const sortItem of sort) {
        for (const [field, direction] of Object.entries(sortItem)) {
          const aVal = a[field]
          const bVal = b[field]
          let cmp: number
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            cmp = aVal - bVal
          } else {
            cmp = String(aVal ?? '').localeCompare(String(bVal ?? ''))
          }
          if (cmp !== 0) return direction === 'desc' ? -cmp : cmp
        }
      }
      return 0
    })
  }

  /** Query records scoped to this schema */
  async find(
    options: {
      selector?: Record<string, unknown>
      limit?: number
      skip?: number
      sort?: Array<Record<string, string>>
    } = {}
  ): Promise<Record<string, unknown>[]> {
    const { selector = {}, limit, skip, sort } = options

    const dataFilter = this.extractDataFilter(selector)
    const hasDataFilter = Object.keys(dataFilter).length > 0

    // Only pass selector to the DB query. Sort, skip, and limit are applied
    // client-side for consistent behavior across RxDB and REST transports
    // (the REST CollectionAccessorAdapter ignores sort/skip/limit in queries).
    const query = this.recordsCollection.find({
      selector: this.rewriteSelector(selector),
    })
    const docs = await query.exec()
    let results = docs.map((doc: unknown) =>
      this.mapFromRecord(this.toPlainObject(doc) as unknown as RecordModel)
    )

    if (hasDataFilter) {
      results = results.filter(doc => VirtualCollectionAdapter.matchesDataFilter(doc, dataFilter))
    }

    // Apply full sort client-side — works on flat (mapped) documents where
    // both top-level and data-bag fields are accessible at the top level.
    if (sort && sort.length > 0) {
      results = VirtualCollectionAdapter.applyClientSort(results, sort)
    }

    if (skip) results = results.slice(skip)
    if (limit) results = results.slice(0, limit)

    return results
  }

  /** Get a single record by ID */
  async findOne(id: string): Promise<Record<string, unknown> | null> {
    const doc = await this.recordsCollection
      .findOne({
        selector: { id, schemaId: this.schemaDefinition.id },
      })
      .exec()

    return doc ? this.mapFromRecord(this.toPlainObject(doc) as unknown as RecordModel) : null
  }

  /**
   * Generate an ID for a new record using the schema-declared prefix.
   * Resolution order:
   * 1. `metadata.idPrefix` from the schema definition (e.g. 'deal')
   * 2. `createId(schemaName)` via ENTITY_TO_PREFIX lookup
   * 3. `createId('records')` as last resort
   */
  private generateId(): string {
    const meta = this.schemaDefinition.metadata as Record<string, unknown> | undefined
    if (typeof meta?.idPrefix === 'string' && meta.idPrefix.length > 0) {
      return `${meta.idPrefix}_${safeNanoid(10)}`
    }
    try {
      return createId(this.schemaDefinition.name)
    } catch {
      return createId('records')
    }
  }

  /** Insert a new record */
  async insert(flatDoc: Record<string, unknown>): Promise<Record<string, unknown>> {
    const id = (flatDoc.id as string) || this.generateId()
    const docWithId = { ...flatDoc, id }
    const record = this.mapToRecord(docWithId)

    // Ensure required fields are present on the record object.
    // mapToRecord propagates id via the contextFields loop, but we set it
    // explicitly as a safeguard against any propagation issue.
    if (!record.id) record.id = id
    const now = new Date().toISOString()
    if (!record.createdAt) record.createdAt = now
    if (!record.updatedAt) record.updatedAt = now

    // The Record schema marks `status`, `parentId`, `assigneeId`, and
    // `workspaceId` as required (they're indexed for project/task-shaped
    // queries). Connector-pulled rows and most simple virtual collections
    // have no semantic counterpart for these fields. Default to empty
    // strings so the records-table schema validates without forcing every
    // virtual-collection caller to construct a project shape they don't
    // need. Empty strings are valid per the schema's `type: 'string'`
    // declaration; indexes treat them as standard tokens.
    const r = record as Record<string, unknown>
    if (r.status === undefined) r.status = ''
    if (r.parentId === undefined) r.parentId = ''
    if (r.assigneeId === undefined) r.assigneeId = ''
    if (r.workspaceId === undefined) r.workspaceId = ''

    const doc = await this.recordsCollection.insert(record)
    return this.mapFromRecord(this.toPlainObject(doc) as unknown as RecordModel)
  }

  /**
   * Update a record by ID with partial data.
   *
   * **Merge semantics:** Existing `data` fields are preserved; only provided
   * fields are overwritten. Setting a field to `null` stores `null` but does
   * NOT delete the key. To remove a field, callers should use a dedicated
   * delete-field operation (not yet implemented).
   *
   * **Immutable fields:** `id`, `schemaId`, `orgId`, `workspaceId`, `userId`,
   * and `createdAt` are silently dropped from updates.
   *
   * **Column routing:** Fields present in TOP_LEVEL_FIELDS are written to
   * real RecordModel columns. Any field NOT in TOP_LEVEL_FIELDS and NOT
   * immutable is packed into the `data` extension blob. The `data` key is
   * only written when there are extension-only fields — callers that update
   * only real columns do not cause an unintentional `data: {}` overwrite.
   *
   * Previously this method maintained a second, shorter hardcoded set of
   * "top-level" fields that excluded RecordModel columns added after the
   * initial implementation (`extended`, `sortOrder`, `priority`, `dueAt`,
   * `amount`, `tenantId`). Those columns were silently routed to the `data`
   * blob, leaving the real DB column NULL. The fix is to use the shared
   * TOP_LEVEL_FIELDS constant which is kept in sync with RecordModel.
   */
  async update(
    id: string,
    updates: Record<string, unknown>
  ): Promise<Record<string, unknown> | null> {
    const doc = await this.recordsCollection
      .findOne({
        selector: { id, schemaId: this.schemaDefinition.id },
      })
      .exec()

    if (!doc) return null

    const existing = this.toPlainObject(doc) as unknown as RecordModel
    const patch: Record<string, unknown> = {}
    // Seed dataUpdates from the existing data blob so that extension fields
    // that are NOT being changed in this update are preserved.
    const dataUpdates: Record<string, unknown> = { ...(existing.data ?? {}) }
    let hasDataUpdates = Object.keys(dataUpdates).length > 0

    for (const [key, value] of Object.entries(updates)) {
      if (IMMUTABLE_FIELDS.has(key)) {
        // Silently drop — callers must not mutate identity/ownership fields.
        continue
      }
      if (TOP_LEVEL_FIELDS.has(key)) {
        // Route to a real RecordModel column.
        patch[key] = value
      } else {
        // True extension field — pack into the data blob.
        dataUpdates[key] = value
        hasDataUpdates = true
      }
    }

    // Only write the data blob when there are extension fields to persist.
    // Omitting the key entirely avoids clobbering the existing data object
    // with an empty {} on updates that only touch real columns.
    if (hasDataUpdates) {
      patch.data = dataUpdates
    }

    const updated = await this.recordsCollection.update(id, patch)
    return this.mapFromRecord(this.toPlainObject(updated) as unknown as RecordModel)
  }

  /**
   * Insert or update a record, handling cross-schema conflicts.
   *
   * Checks for existing documents proactively before insert to handle
   * cross-org/cross-schema scenarios where the same external ID exists
   * under a different schemaId/orgId. Falls back to CONFLICT catch for
   * race conditions.
   *
   * Use this for sync workflows where the same external ID may have been
   * previously imported under a different org or schema context.
   */
  async upsert(flatDoc: Record<string, unknown>): Promise<Record<string, unknown>> {
    const id = flatDoc.id as string

    // Fast path: record already belongs to this schema — update it
    const existing = await this.recordsCollection
      .findOne({ selector: { id, schemaId: this.schemaDefinition.id } })
      .exec()

    if (existing) {
      const plain = this.toPlainObject(existing) as unknown as RecordModel
      const record = this.mapToRecord(flatDoc)
      // Merge data fields (preserve existing, overwrite with new)
      const mergedData = { ...(plain.data ?? {}), ...(record.data ?? {}) }
      const patch: Record<string, unknown> = { ...record, data: mergedData }
      delete (patch as Record<string, unknown>).id // id is immutable primary key
      const updated = await this.recordsCollection.update(id, patch)
      return this.mapFromRecord(this.toPlainObject(updated) as unknown as RecordModel)
    }

    // Check if document exists under ANY schema (cross-org reclaim)
    const anyExisting = await this.recordsCollection.findOne({ selector: { id } }).exec()

    if (anyExisting) {
      // Reclaim: overwrite with this adapter's schema and the caller's org context
      const record = this.mapToRecord(flatDoc)
      const fullPatch: Record<string, unknown> = { ...record }
      delete (fullPatch as Record<string, unknown>).id
      const updated = await this.recordsCollection.update(id, fullPatch)
      return this.mapFromRecord(this.toPlainObject(updated) as unknown as RecordModel)
    }

    // New document — insert
    try {
      const record = this.mapToRecord(flatDoc)
      const doc = await this.recordsCollection.insert(record)
      return this.mapFromRecord(this.toPlainObject(doc) as unknown as RecordModel)
    } catch (error: unknown) {
      // Race condition fallback: document appeared between findOne and insert
      const msg = error instanceof Error ? error.message : String(error)
      if (msg.includes('CONFLICT') || msg.includes('conflict') || msg.includes('409')) {
        const record = this.mapToRecord(flatDoc)
        const fullPatch: Record<string, unknown> = { ...record }
        delete (fullPatch as Record<string, unknown>).id
        const updated = await this.recordsCollection.update(id, fullPatch)
        return this.mapFromRecord(this.toPlainObject(updated) as unknown as RecordModel)
      }
      throw error
    }
  }

  /** Delete a record by ID */
  async remove(id: string): Promise<boolean> {
    // Verify document belongs to this schema before deleting
    const doc = await this.recordsCollection
      .findOne({
        selector: { id, schemaId: this.schemaDefinition.id },
      })
      .exec()

    if (!doc) return false
    return this.recordsCollection.delete(id)
  }

  /** Count records matching a selector */
  async count(selector: Record<string, unknown> = {}): Promise<number> {
    // Always count client-side via find() — the REST
    // CollectionAccessorAdapter does not implement .count().
    const docs = await this.find({ selector })
    return docs.length
  }

  /**
   * Execute an aggregate (GROUP BY / HAVING) query.
   *
   * Delegates directly to the underlying `RecordCollection.aggregate()`.
   * Only REST-backed collections (D1 worker) implement this method —
   * local RxDB collections do not support server-side aggregation.
   *
   * @param spec - Aggregate request spec forwarded verbatim to the worker.
   * @throws Error if the underlying collection does not support aggregate.
   */
  async aggregate(spec: Record<string, unknown>): Promise<{ documents: Record<string, unknown>[] }> {
    if (typeof this.recordsCollection.aggregate !== 'function') {
      throw new Error(
        `aggregate is not supported by the underlying collection for schema "${this.schemaDefinition.name}". ` +
        'Only REST-backed (D1) collections support server-side aggregation.'
      )
    }
    // Mirror find()/findOne(): inject schemaId so the D1 worker filters to the
    // correct VCA schema. Without this, an aggregate call on 'journallines'
    // would operate over ALL records rows for the org, ignoring the schema boundary.
    const selector = ((spec.selector as Record<string, unknown>) ?? {})
    const scopedSpec: Record<string, unknown> = {
      ...spec,
      selector: {
        // Spread caller selector first, then assert our schemaId so the
        // adapter's schema boundary always takes precedence over any
        // caller-supplied schemaId value.
        ...selector,
        schemaId: this.schemaDefinition.id,
      },
    }
    return this.recordsCollection.aggregate(scopedSpec)
  }
}

/**
 * Wrap a raw RxDB collection to satisfy the RecordCollection interface.
 *
 * RxDB collections have document-level update/delete (doc.patch, doc.remove)
 * but not collection-level update(id, patch) or delete(id). This wrapper
 * bridges that gap so VirtualCollectionAdapter works in both browser (RxDB)
 * and REST (CollectionAccessorAdapter) contexts.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function wrapRxCollection(rxCollection: any): RecordCollection {
  return {
    find: query => rxCollection.find(query),
    findOne: query => rxCollection.findOne(query),
    insert: doc => rxCollection.insert(doc),
    update: async (id: string, patch: Record<string, unknown>) => {
      const doc = await rxCollection.findOne({ selector: { id } }).exec()
      if (!doc) throw new Error(`Document ${id} not found`)
      return doc.patch(patch)
    },
    delete: async (id: string) => {
      const doc = await rxCollection.findOne({ selector: { id } }).exec()
      if (!doc) return false
      await doc.remove()
      return true
    },
  }
}
